import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { intelligenceQAIqa001Api, type Iqa001Status, type SprintDashboard, type SprintValidation } from '../../api/intelligence-qa-iqa001';

type Tab = 'overview' | 'modules' | 'rules' | 'providers' | 'jobs' | 'gates' | 'status';

export function IntelligenceQAIqa001Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [rules, setRules] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Iqa001Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-rose-200 bg-white dark:border-rose-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-rose-700 dark:text-rose-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, rl, prov, jb, gt, st] = await Promise.all([
        intelligenceQAIqa001Api.dashboard(), intelligenceQAIqa001Api.qaModules(),
        intelligenceQAIqa001Api.validationRules(), intelligenceQAIqa001Api.providers(),
        intelligenceQAIqa001Api.backgroundJobs(), intelligenceQAIqa001Api.qualityGates(),
        intelligenceQAIqa001Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setRules(rl); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await intelligenceQAIqa001Api.validate({
        no_conflicting_recommendations: true, every_recommendation_validated: true,
        evidence_complete: true, safety_checks_passed: true, business_goals_aligned: true,
        marketplace_dna_current: true, knowledge_graph_updated: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Intelligence QA passes all release gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'QA Modules' },
    { id: 'rules', label: 'Validation Rules' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-rose-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>IQA-000001 · P0 · Mandatory Before Customer Release</p>
        <h1 className="text-2xl font-semibold">Intelligence Quality Assurance Framework</h1>
        <p className={`text-sm ${muted}`}>Consistent · Accurate · Evidence-based · Non-conflicting · Safe · Explainable</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/recommendation-validation-val001" className={`${accent} hover:underline`}>VAL-000001</Link>
          <span className={muted}>→</span>
          <Link to="/intelligence-qa" className={`${accent} hover:underline`}>Live QA Command Center</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-sm text-rose-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-rose-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Every Recommendation Passes Intelligence QA</h2>
            <p className={`mt-2 text-sm ${muted}`}>One coordinated intelligence system — conflict detection, consolidation, quality scoring, safety engine and auditable decision governance.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'QA Modules', value: dashboard.qa_modules },
              { label: 'Validation Rules', value: dashboard.validation_rules }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-rose-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Release Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-rose-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}

      {tab === 'rules' && rules.map((r) => (
        <div key={r.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{r.name}</p>{r.blocking && <p className={`text-xs ${muted}`}>Blocking</p>}</div>
      ))}

      {tab === 'providers' && providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs ${muted}`}>{p.interface}</p></div>
      ))}

      {tab === 'jobs' && jobs.map((j) => (
        <div key={j.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{j.name}</p><p className={`text-xs ${muted}`}>{j.schedule}</p></div>
      ))}

      {tab === 'gates' && gates.map((g) => (
        <div key={g.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{g.name}</p>{g.blocking && <p className={`text-xs ${muted}`}>Blocking</p>}</div>
      ))}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}><pre className="overflow-auto text-xs">{JSON.stringify(status, null, 2)}</pre></div>
      )}
    </div>
  );
}
